/**
 * Expected KVP keys. This list is the source of truth for which keys *should*
 * exist; it is not a source of values.
 *
 * Maintain this file whenever the system starts or stops expecting a key:
 *   - tool descriptions (`tool.<name>`)
 *   - the system default prompt set (`system_prompt.default_prompt_set`)
 *
 * Which model each task uses is not KVP: see Settings → Models.
 *   - `user_action.*`, `skill.*`, `system_state.*`, `system_prompt.*`
 *
 * How it is used:
 *   - Settings → KVP unions this list with rows from the database.
 *   - A key that is in this registry but not in the database is shown greyed
 *     out and is editable. Saving registers it (including an empty string).
 *   - A registered key with an empty string is not the same as an unregistered
 *     key. Both are valid, different states.
 *   - Deleting a registered key that is still listed here returns it to grey.
 *   - Extra database keys that are not in this list still appear (ad-hoc keys
 *     added in Settings).
 *
 * Values live in `public.kvp` and are edited in Settings. Runtime KVP reads
 * (`backend/src/utils/kvp.py`) use the database only.
 *
 * See also: `frontend/src/app/workspace/i18n/i18nKeyRegistry.ts`.
 */

export const EXPECTED_KVP_KEYS = [
    // system prompt
    "system_prompt.eyka_infrastructure",
    "system_prompt.conversation_analysis",
    "system_prompt.memory_headline",
    "system_prompt.initial_conversation_status_message_generation",
    "system_prompt.preference_reduction",
    "system_prompt.ticketing_agent",
    "system_prompt.tool_error_agent",
    "system_prompt.skill_author",

    // tools
    "tool.call_dynamic_tool",
    "tool.start_bug_report",
    "tool.validate_citation",
    "tool.cruq_tool",
    "tool.current_time_tool",
    "tool.get_conversation_constants",
    "tool.get_dynamic_tool_definitions",
    "tool.modify_user_preferences_tool",
    "tool.set_selected_objects_tool",
    "tool.skills_tool",

    // system default prompt set (fallback for organisation prompt sets)
    "system_prompt.default_prompt_set",

    // user actions
    "user_action.create_artifact",
    "user_action.create_tag",
    "user_action.create_event",
    "user_action.create_document",
    "user_action.delete_artifact",
    "user_action.delete_tag",
    "user_action.delete_event",
    "user_action.delete_document",
    "user_action.update_document",
    "user_action.revision_artifact",
    "user_action.user_changes_artifact",
    "user_action.update_artifact",
    "user_action.artifact_variable_name_update",
    "user_action.revisions_discarded_artifact",
    "user_action.revisions_discarded_tag",
    "user_action.update_event",
    "user_action.update_tag",
    "user_action.fallback",

    // skills / system state
    "skill.create_artifact",
    "system_state.selected_objects",
] as const;

export type ExpectedKvpKey = (typeof EXPECTED_KVP_KEYS)[number];

export const EXPECTED_KVP_KEY_SET: ReadonlySet<string> = new Set(EXPECTED_KVP_KEYS);
